/* eslint-disable @typescript-eslint/no-explicit-any */
"use strict";

// ... (all interfaces and constants remain the same as before) ...
interface GameState {
  gameStarted: boolean;
  flippedCards: number;
  totalFlips: number;
  totalTime: number;
  loop: ReturnType<typeof setInterval> | null;
  images: Image[];
}

interface ControlState {
  boardSize: string;
  category: string;
  isGameWithImages: boolean;
  theme?: string;
  bestScores?: ScoreModel;
  classroomMode?: boolean;
}

interface Category {
  id: number;
  name: string;
  link: string;
  icon: string;
}

interface Level {
  id: number;
  name: string;
}

interface Image {
  id: number;
  symbol?: string;
  description: string;
}

interface Score {
  score: number;
  time: string;
}
interface ScoreLevel {
  id: number;
  allScores: Score[];
  name: string;
}
interface ScoreModel {
  levels: ScoreLevel[];
}

const initialScoreModel: ScoreModel = {
  levels: [
    { id: 0, allScores: [], name: "3x2" },
    { id: 1, allScores: [], name: "4x3" },
    { id: 2, allScores: [], name: "4x4" },
    { id: 3, allScores: [], name: "5x4" },
    { id: 4, allScores: [], name: "6x5" },
    { id: 5, allScores: [], name: "8x5" },
    { id: 6, allScores: [], name: "8x6" }
  ]
};

const shareData = {
  title: "MemoryMatching.com",
  text: "Play free memory matching games!",
  url: "https://www.memorymatching.com"
};


class MemoryGame {
  gameState: GameState;
  controlState: ControlState;
  levels: Level[] = this.getInitialLevels();
  categories: Category[] = this.getInitialCategories();
  themes = ["theme-red", "theme-orange", "theme-green", "theme-indigo", "theme-sky"];
  categoryFromURL: string | null;

  constructor() {
    this.categoryFromURL = window.location.pathname
      .replace("/", "")
      .replace(/-/g, "")
      .replace(".html", "");

    this.controlState = this.getInitialControlState();
    this.readBestScores();
    this.setThemeState();

    this.gameState = {
      gameStarted: false,
      flippedCards: 0,
      totalFlips: 0,
      totalTime: 0,
      loop: null,
      images: []
    };

    this.bindEventsOnce();

    this.loadImagesForCurrentCategory().then(() => {
      this.gameState = this.getInitialGameState();
      if (typeof this.controlState.classroomMode === 'undefined') {
        this.controlState.classroomMode = false;
      }
      this.createNewGame();
    });
  }

  private async loadImagesForCurrentCategory(): Promise<void> {
    console.log("Category:", this.controlState.category);
    const url = `/data/${this.controlState.category}.json`;
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`${url} – ${res.status} ${res.statusText}`);
      (window as any).sourceImages = (await res.json()) as Image[];
    } catch (err) {
      console.error("Deck load failed:", err);
      (window as any).sourceImages = [];
    }
  }

  private setThemeState(): void {
    if (this.controlState.theme) {
      this.controlState.theme =
        this.themes.filter((t) => t !== this.controlState.theme)[
          Math.floor(Math.random() * (this.themes.length - 1))
        ];
    } else {
      this.controlState.theme = this.themes[Math.floor(Math.random() * this.themes.length)];
    }
  }

  getInitialControlState(overriddenSize?: string): ControlState {
    const state: ControlState = {
      boardSize: overriddenSize ?? "5x4",
      category: "foodanddrinks",
      isGameWithImages: false,
      classroomMode: false,
    };

    if (this.categoryFromURL) {
      state.category = this.categoryFromURL;
      if (state.category === "dailychallenge") state.boardSize = "9x6";

      const simple = state.category.replace(/-/g, "");
      if (
        !this.categories.some((c) => c.link.replace(/-/g, "") === simple) &&
        state.category !== "dailychallenge"
      ) {
        state.isGameWithImages = true;
      }
    }
    return state;
  }

  getInitialGameState(): GameState {
    const [rows, cols] = this.controlState.boardSize.split("x").map(Number);
    const deck: Image[] = (window as any).sourceImages ?? [];
    const picks = this.pickRandom(deck, (rows * cols) / 2);
    const cards = this.shuffle([...picks, ...picks]);
    return {
      gameStarted: false,
      flippedCards: 0,
      totalFlips: 0,
      totalTime: 0,
      loop: null,
      images: cards
    };
  }

  // ADDED: Helper to update classroom toggle visual state
  private updateClassroomModeToggleVisual(isActive: boolean): void {
    const classroomToggleCheckbox = document.getElementById('classroomModeToggle') as HTMLInputElement | null;
    if (classroomToggleCheckbox) {
        const toggleButtonDiv = classroomToggleCheckbox.parentElement; // The div wrapping checkbox and label
        if (toggleButtonDiv) {
            if (isActive) {
                toggleButtonDiv.classList.remove('bg-white', 'text-gray-800');
                toggleButtonDiv.classList.add('bg-sky-600', 'text-white');
            } else {
                toggleButtonDiv.classList.remove('bg-sky-600', 'text-white');
                toggleButtonDiv.classList.add('bg-white', 'text-gray-800');
            }
        }
    }
  }

  buildHTML(): void {
    const boardContainer = document.querySelector(".board-container") as HTMLElement;
    const observer = new MutationObserver(() => this.bindEvents());
    observer.observe(boardContainer, { childList: true });

    const [rows, cols] = this.controlState.boardSize.split("x").map(Number);
    const boardHTML = `
      <div class="board"
           style="grid-template-columns: repeat(${cols},1fr);
                  grid-template-rows: repeat(${rows},1fr)">
        ${this.gameState.images
          .map((img, index) => {
            let cardFrontContent = '★';
            if (this.controlState.classroomMode) {
              const rowIndex = Math.floor(index / cols);
              const colIndex = index % cols;
              cardFrontContent = String.fromCharCode(65 + rowIndex) + (colIndex + 1);
            }
            return `
              <div class="card"
                   data-id="${img.id}"
                   data-description="${img.description}"
                   ${this.controlState.isGameWithImages ? "" : `data-symbol="${img.symbol}"`}>
                <div class="card-front">${cardFrontContent}</div>
                ${
                  this.controlState.isGameWithImages
                    ? `<div class="card-back"><img src="/images/${this.controlState.category}/${img.id}.jpg" /></div>`
                    : `<div class="card-back">${img.symbol}</div>`
                }
              </div>`;
            }
          )
          .join("")}
      </div>
    `;
    const fragment = new DOMParser().parseFromString(boardHTML, "text/html");
    boardContainer.classList.remove("flipped-board");
    boardContainer.replaceChildren(fragment.querySelector(".board")!);
    observer.disconnect();

    (document.querySelector(".moves") as HTMLElement).innerText = "Moves: 0";
    (document.querySelector(".timer") as HTMLElement).innerHTML =
      `<span className="bold">Time: </span><span>00:</span><span>00</span>`;
    const bestCell = document.querySelector(".current-best-score") as HTMLElement;
    const lvl = this.controlState.bestScores?.levels.find((l) => l.name === this.controlState.boardSize);
    const bestMoves = lvl?.allScores?.map((s) => s.score) ?? [];
    bestCell.textContent = bestMoves.length ? `${Math.min(...bestMoves)}` : "---";
    const body = document.body;
    [...body.classList].filter((c) => c.startsWith("theme")).forEach((c) => body.classList.remove(c));
    body.classList.add(this.controlState.theme!);
    
    const classroomToggle = document.getElementById('classroomModeToggle') as HTMLInputElement | null;
    if (classroomToggle) {
        classroomToggle.checked = !!this.controlState.classroomMode;
    }
    // UPDATED: Call visual update helper
    this.updateClassroomModeToggleVisual(!!this.controlState.classroomMode);
  }

  resetGameState(boardSize?: string): void {
    const prevClassroomMode = this.controlState?.classroomMode; 
    if (boardSize) {
      this.controlState = this.getInitialControlState(boardSize);
      if (prevClassroomMode !== undefined) {
        this.controlState.classroomMode = prevClassroomMode;
      }
      this.readBestScores();
    }
    if (this.controlState && typeof this.controlState.classroomMode === 'undefined') {
       this.controlState.classroomMode = false;
    }
    this.setThemeState();
    this.gameState = this.getInitialGameState();
  }

  createNewGame(boardSize?: string): void {
    this.resetGameState(boardSize);
    this.buildHTML();
    this.bindEvents();
  }

  bindEventsOnce(): void {
    const qs = (sel: string) => document.querySelector(sel) as HTMLElement;
    qs(".controls-new-game")?.addEventListener("click", this.handleNewGameClick.bind(this));
    qs(".native-share-button")?.addEventListener("click",this.handleNativeShareButtonClick.bind(this));
    qs(".controls-size")?.addEventListener("click", this.handleControlsSizeClick.bind(this));
    qs(".modal-size-close")?.addEventListener("click", this.handleModalSizeCloseClick.bind(this));
    qs(".modal-background")?.addEventListener("click", this.handleModalSizeCloseClick.bind(this));
    qs(".controls-category")?.addEventListener("click", this.handleControlsCategoryClick.bind(this));
    qs(".modal-category-close")?.addEventListener("click",this.handleModalCategoryCloseClick.bind(this));
    qs(".modal-category-background")?.addEventListener("click",this.handleModalCategoryCloseClick.bind(this));
    qs(".header-menu-control")?.addEventListener("click",this.handleHeaderMenuControlClick.bind(this));
    qs(".modal-main-menu-close")?.addEventListener("click",this.handleModalMainMenuCloseClick.bind(this));
    (document.getElementById('classroomModeToggle') as HTMLElement)?.addEventListener(
        'change', 
        this.handleClassroomModeToggle.bind(this)
    );
  }

  bindEvents(): void {
    document.querySelectorAll(".card").forEach((card) =>
      card.addEventListener("click", () => this.flipCard(card))
    );
  }

  handleClassroomModeToggle(): void {
    const checkbox = document.getElementById('classroomModeToggle') as HTMLInputElement;
    this.controlState.classroomMode = checkbox.checked;
    // UPDATED: Call visual update helper
    this.updateClassroomModeToggleVisual(checkbox.checked);

    if(typeof gtag === 'function'){
        gtag("event", "classroom_mode_toggled", { 
            event_category: "game_settings", 
            label: checkbox.checked ? "enabled" : "disabled" 
        });
    }
    this.createNewGame(this.controlState.boardSize);
  }

  handleHeaderMenuControlClick(): void {
    if(typeof gtag === 'function'){
    gtag("event", "main_menu_open_modal", { event_category: "homepage_engagement" });}
    const modalMainMenu = document.querySelector(".modal-main-menu");
    if (modalMainMenu) modalMainMenu.classList.add("modal-opened");
  }
  handleModalMainMenuCloseClick(): void {
    const modalMainMenu = document.querySelector(".modal-main-menu");
    if (modalMainMenu) modalMainMenu.classList.remove("modal-opened");
  }

  handleControlsSizeClick(): void {
    if(typeof gtag === 'function'){
    gtag("event", "change_size_open_modal", { event_category: "homepage_engagement" });}
    this.loadBoardSizes();
    this.bindSizeEvents();
    document.querySelector(".modal-size")!.classList.add("modal-opened");
    document.querySelector(".modal-background")!.classList.add("modal-opened");
  }
  handleModalSizeCloseClick(): void {
    document.querySelector(".modal-size")!.classList.remove("modal-opened");
    document.querySelector(".modal-background")!.classList.remove("modal-opened");
    this.removeSizeEvents();
  }

    handleControlsCategoryClick(): void {
    document.body.style.overflow = 'hidden';
    if(typeof gtag === 'function'){
    gtag("event", "change_category_open_modal", { event_category: "homepage_engagement" });}
    this.loadCategories();
    this.bindCategoryEvents();
    document.querySelector(".modal-category")!.classList.add("modal-opened");
    document.querySelector(".modal-category-background")!.classList.add("modal-opened");
  }
    handleModalCategoryCloseClick(): void {
        document.body.style.overflow = '';
    document.querySelector(".modal-category")!.classList.remove("modal-opened");
    document.querySelector(".modal-category-background")!.classList.remove("modal-opened");
    this.removeCategoryEvents();
  }

  async handleNativeShareButtonClick(): Promise<void> {
    try {
      await navigator.share(shareData);
      if(typeof gtag === 'function'){
      gtag("event", "friend_share_button_clicked", { event_category: "homepage_engagement" });}
    } catch {
      console.log("Share failed – copy the link manually.");
    }
  }

  handleNewGameClick(): void {
    if(typeof gtag === 'function'){
    gtag("event", "new_game_created", { event_category: "homepage_engagement" });}
    this.createNewGame(this.controlState.boardSize);
  }
  handleNewGameAfterWinClick(): void {
    if(typeof gtag === 'function'){
    gtag("event", "new_game_created_after_win_retry", { event_category: "homepage_engagement" });}
    this.createNewGame(this.controlState.boardSize);
  }
  handleNewNextLevelGameAfterWinClick(): void {
    if(typeof gtag === 'function'){
    gtag("event", "new_game_created_after_win_next", { event_category: "homepage_engagement" });}
    const current = this.levels.find((l) => l.name === this.controlState.boardSize)!;
    const next = this.levels[Math.min(current.id + 1, this.levels.length - 1)];
    this.createNewGame(next.name);
  }

  bindSizeEvents(): void {
    document.querySelectorAll(".size-item").forEach((card) =>
      card.addEventListener("click", () => this.handleSizeCardClick(card as HTMLElement))
    );
  }
  removeSizeEvents(): void {
    document.querySelectorAll(".size-item").forEach((card) => {
      card.removeEventListener("click", () => this.handleSizeCardClick(card as HTMLElement));
    });
  }
  handleSizeCardClick(card: HTMLElement): void {
    const newSize = card.innerText;
    this.createNewGame(newSize); 
    this.removeSizeEvents();
    document.querySelector(".modal-size")!.classList.remove("modal-opened");
    document.querySelector(".modal-background")!.classList.remove("modal-opened");
  }

  bindCategoryEvents(): void {
    document.querySelectorAll(".category-item").forEach((card) =>
      card.addEventListener("click", () => this.handleCategoryCardClick(card as HTMLElement))
    );
  }
  removeCategoryEvents(): void {
    document.querySelectorAll(".category-item").forEach((card) => {
      card.removeEventListener("click", () => this.handleCategoryCardClick(card as HTMLElement));
    });
  }
  handleCategoryCardClick(card: HTMLElement): void {
    if(typeof gtag === 'function'){
    gtag("event", "change_category_completed", { event_category: "homepage_engagement" });}
    this.removeCategoryEvents();
    window.open(card.dataset.link!, "_self");
  }

  loadBoardSizes(): void {
    const out =
      '<div class="size-list">' +
      this.levels.map((l) => `<div class="size-item"><span>${l.name}</span></div>`).join("") +
      "</div>";
    (document.querySelector(".modal-size-content") as HTMLElement).innerHTML = out;
  }
  loadCategories(): void {
      fetch('/partials/modal-category-expanded.html')
          .then(res => res.text())
          .then(html => {
              const container = document.querySelector(".modal-category-content") as HTMLElement;
              container.innerHTML = html;
          });
  }

  startGame(): void {
    this.gameState.gameStarted = true;
    const timerEl = document.querySelector(".timer") as HTMLElement;
    this.gameState.loop = setInterval(() => {
      this.gameState.totalTime++;
      const m = Math.floor(this.gameState.totalTime / 60).toString().padStart(2, "0");
      const s = (this.gameState.totalTime % 60).toString().padStart(2, "0");
      timerEl.innerHTML = `<span className="bold">Time: </span><span>${m}:</span><span>${s}</span>`;
    }, 1000);
  }

  flipBackCards(): void {
    document.querySelectorAll(".card.flipped:not(.matched)").forEach((c) => c.classList.remove("flipped"));
    this.gameState.flippedCards = 0;
  }

  flipCard(card: Element): void {
  if (card.classList.contains("matched") || card.classList.contains("flipped")) return;

  // Prevent flipping if two cards are still showing and waiting to be flipped back
  const flipped = document.querySelectorAll(".card.flipped:not(.matched)");
  if (flipped.length === 2) {
    this.flipBackCards(); // flip back old ones immediately
  }

  // Flip current card
  card.classList.add("flipped");

  if (!this.gameState.gameStarted) this.startGame();
  this.gameState.flippedCards++;
  const flippedNow = document.querySelectorAll(".card.flipped:not(.matched)").length;
if (flippedNow === 2) {
  this.gameState.totalFlips++;
}

  (document.querySelector(".moves") as HTMLElement).innerText = `Moves: ${this.gameState.totalFlips}`;
  const snackbar = document.getElementsByClassName("snackbar")[0] as HTMLElement | undefined;
  if (snackbar) snackbar.className = snackbar.className.replace("show", "");

  const nowFlipped = document.querySelectorAll(".card.flipped:not(.matched)");
  if (nowFlipped.length === 2) {
    const [cardA, cardB] = nowFlipped as unknown as HTMLElement[];
    if (cardA.dataset.id === cardB.dataset.id) {
      cardA.classList.add("matched");
      cardB.classList.add("matched");
      this.gameState.flippedCards = 0;

      if (snackbar) {
        snackbar.innerHTML = `<div><span class="discovered">Discovered</span></div>
        <div class="discovered-container">${
          this.controlState.isGameWithImages ? "" : `<span class="discovered-flag">${cardA.dataset.symbol}</span>`
        }<span>${cardA.dataset.description}</span></div>`;
        snackbar.classList.add("show");
      }
    }
  }

  if (!document.querySelectorAll(".card:not(.matched)").length) {
    this.handleWin();
  }
}


  handleWin(): void {
    const snackbar = document.getElementsByClassName("snackbar")[0] as HTMLElement | undefined;
    if(snackbar) setTimeout(() => (snackbar.className = snackbar.className.replace("show", "")), 3000);

    const m = Math.floor(this.gameState.totalTime / 60).toString().padStart(2, "0");
    const s = (this.gameState.totalTime % 60).toString().padStart(2, "0");
    const timeStr = `${m}:${s}`;
    const boardContainer = document.querySelector(".board-container") as HTMLElement;
    boardContainer.classList.add("flipped-board");

    const phrases = [ /* ... your phrases ... */ ];
    const randomPhrase = phrases[Math.floor(Math.random() * phrases.length)];
    const promotionHTML = `<!-- ... your promotion HTML ... -->`; // Your existing promotionHTML

    if (this.controlState.category === "dailychallenge") {
        boardContainer.innerHTML = `
        <div class="win-text">
            <div class="win-info"><div class="win-icon">🏆</div><div class="headline">You win!</div>
            <div class="current-result">
                <div class="current-result-item">Moves:<div class="highlight">${this.gameState.totalFlips}</div></div>
                <div class="current-result-item">Time:<div class="highlight">${timeStr}</div></div>
            </div></div>
            <div class="ranking-header">Great job! You completed a daily challenge. Click ‘Retry’ to play new daily challenge or click ‘All games’ to play other memory games.</div>
            <div class="best-score-actions"><button class="play-new-game">Retry</button></div>
            ${promotionHTML}
        </div>`;
        document.querySelector(".play-new-game")!.addEventListener("click", this.handleNewGameAfterWinClick.bind(this));
    } else {
        const lvl = this.controlState.bestScores!.levels.find((l) => l.name === this.controlState.boardSize)!;
        lvl.allScores.push({ score: this.gameState.totalFlips, time: timeStr });
        lvl.allScores.sort((a, b) => a.score === b.score ? (a.time > b.time ? 1 : -1) : a.score - b.score);
        lvl.allScores = lvl.allScores.slice(0, 3);
        this.saveBestScores(this.controlState.bestScores!);
        const bestTable = this.buildBestScoreTableHTML(lvl.allScores);
        const bestMoves = Math.min(...lvl.allScores.map((s) => s.score));
        (document.querySelector(".current-best-score") as HTMLElement).textContent = `${bestMoves}`;
        boardContainer.innerHTML = `
        <div class="win-text">
            <div class="win-info"><div class="win-icon">🏆</div><div class="headline">You win!</div>
            <div class="current-result">
                <div class="current-result-item">Moves:<div class="highlight">${this.gameState.totalFlips}</div></div>
                <div class="current-result-item">Time:<div class="highlight">${timeStr}</div></div>
            </div></div>
            <div class="ranking-header">Your ranking for board size: <span class="ranking-boardsize">${this.controlState.boardSize}</span></div>
            <div>${bestTable}</div>
            <div class="best-score-actions"><button class="play-new-game">Retry</button><button class="play-next-level">Play next</button></div>
            ${promotionHTML}
        </div>`;
        document.querySelector(".play-new-game")!.addEventListener("click", this.handleNewGameAfterWinClick.bind(this));
        document.querySelector(".play-next-level")!.addEventListener("click", this.handleNewNextLevelGameAfterWinClick.bind(this));
    }
    if (this.gameState.loop) clearInterval(this.gameState.loop);
    if (typeof gtag === 'function') {
        gtag("event", "game_won", { event_category: "homepage_engagement", label: this.controlState.boardSize });
    }
  }

  buildBestScoreTableHTML(bestScores: Score[]): string { /* ... same as before ... */ 
    const rows = bestScores
      .map((s, idx) => {
        const medal = ["🥇", "🥈", "🥉"][idx] ?? "";
        return `<tr><td>${medal}</td><td>${s.score}</td><td>${s.time}</td></tr>`;
      })
      .join("");
    return `<table class="best-score-table">
      <tr><th>Rank:</th><th>Moves:</th><th>Time:</th></tr>${rows}
    </table>`;
  }
  shuffle<T>(arr: T[]): T[] { /* ... same as before ... */ 
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  }
  pickRandom<T>(arr: T[], n: number): T[] { /* ... same as before ... */ 
    const pool = [...arr];
    const out: T[] = [];
    while (out.length < n && pool.length) {
      const idx = Math.floor(Math.random() * pool.length);
      out.push(pool.splice(idx, 1)[0]);
    }
    return out;
  }
  readBestScores(): void { /* ... same as before ... */ 
    const saved = localStorage.getItem("scores");
    this.controlState.bestScores = saved ? (JSON.parse(saved) as ScoreModel) : initialScoreModel;
    if (!saved) this.saveBestScores(initialScoreModel);
  }
  saveBestScores(model: ScoreModel): void { /* ... same as before ... */ 
    this.controlState.bestScores = model;
    localStorage.setItem("scores", JSON.stringify(model));
  }
  getInitialLevels(): Level[] { /* ... same as before ... */ 
    return [
      { id: 0, name: "3x2" }, { id: 1, name: "4x3" }, { id: 2, name: "4x4" },
      { id: 3, name: "5x4" }, { id: 4, name: "6x5" }, { id: 5, name: "8x5" },
      { id: 6, name: "8x6" }
    ];
  }
  getInitialCategories(): Category[] { /* ... same as before ... */ 
    return [
        { id: 0, name: "Flags", link: "flags", icon: "🏁" },
        { id: 1, name: "Food and drinks", link: "food-and-drinks", icon: "🍉" },
        { id: 2, name: "Animals", link: "animals", icon: "🐢" },
        { id: 3, name: "Shapes", link: "shapes", icon: "💠" },
        { id: 4, name: "Numbers", link: "numbers", icon: "123" },
        { id: 5, name: "Letters", link: "letters", icon: "abc" },
        { id: 6, name: "Fruits and vegetables", link: "fruits-and-vegetables", icon: "🍊" },
        { id: 7, name: "Clothing", link: "clothing", icon: "🧥" },
        { id: 8, name: "Hand signs", link: "hand-signs", icon: "🤌" },
        { id: 9, name: "Sport", link: "sport", icon: "🏀" },
        { id: 10, name: "Sky and weather", link: "sky-and-weather", icon: "⛈" },
        { id: 11, name: "Time", link: "time", icon: "⌛" },
        { id: 12, name: "Transport", link: "transport", icon: "🛩" },
        { id: 13, name: "Travel and places", link: "travel-and-places", icon: "🌍" },
        { id: 14, name: "People", link: "people", icon: "👵" },
        { id: 15, name: "Tools", link: "tools", icon: "🔧" }
      ];
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const boardContainer = document.querySelector(".board-container") as HTMLElement;
  if(boardContainer) { new MemoryGame(); }
  
  // ... (rest of your DOMContentLoaded, including sidebar logic, remains the same) ...
  const COLLAPSED_KEY = 'sidebarCollapsed';
  const hamburger   = document.getElementById('hamburger') as HTMLButtonElement | null;
  const sidebar     = document.getElementById('sidebar')   as HTMLElement | null;
  const overlay     = document.getElementById('overlay')   as HTMLElement | null;
  const mainContent = document.getElementById('mainContent') as HTMLElement | null;

  if (hamburger && sidebar && overlay && mainContent) {
      const isMobile = () => window.innerWidth <= 768;
      const saved = localStorage.getItem(COLLAPSED_KEY) === 'true';
      if (saved && !isMobile()) {
        sidebar.classList.add('collapsed');
        mainContent.classList.add('collapsed');
      }
      const updateView = () => {
        if (isMobile()) {
          sidebar.classList.remove('collapsed');
          mainContent.classList.remove('collapsed');
        } else {
          sidebar.classList.remove('open');
          overlay.classList.remove('active');
        }
      };
      hamburger.addEventListener('click', () => {
        if (isMobile()) {
          sidebar.classList.toggle('open');
          overlay.classList.toggle('active');
        } else {
          sidebar.classList.toggle('collapsed');
          mainContent.classList.toggle('collapsed');
          localStorage.setItem(COLLAPSED_KEY, String(sidebar.classList.contains('collapsed')));
        }
      });
      overlay.addEventListener('click', () => {
        sidebar.classList.remove('open');
        overlay.classList.remove('active');
      });
      window.addEventListener('resize', updateView);
      updateView();
  }

  const links = document.querySelectorAll('.sidebar-link') as NodeListOf<HTMLAnchorElement>;
  links.forEach(link => {
    if (link.href) { 
        try {
            const linkPath = new URL(link.href).pathname.replace(/\/$/, '');
            const currentPath = window.location.pathname.replace(/\/$/, '');
            if (linkPath === currentPath) {
              link.setAttribute('aria-current', 'page');
              link.classList.add('active');
            } else {
              link.removeAttribute('aria-current');
              link.classList.remove('active');
            }
        } catch (e) { /* console.warn("Could not parse URL for sidebar link:", link.href, e); */ }
    }
  });
  
  const shareEntryButton = document.getElementById('shareEntry');
  if (shareEntryButton) {
      shareEntryButton.addEventListener('click', (e) => {
        e.preventDefault();
        if (navigator.share) {
          navigator.share({
            title: 'MemoryMatching.com',
            text: 'Check out this fun memory game!',
            url: window.location.href,
          }).catch(err => { console.warn('Sharing cancelled or failed:', err); });
        } else {
          alert('Copy this link and share with a friend:\n' + window.location.href);
        }
      });
  }
});